import { WarningAmberOutlined } from '@mui/icons-material'
import { Box, TextField, Tooltip, Typography } from '@mui/material'
import { RJSFValidationError } from '@rjsf/utils'
import { DialogCloseSaveButtons } from 'components/Common/DialogActionButtons/DialogCloseSaveButtons'
import ValueLabel from 'components/Common/ValueLabel/ValueLabel'
import { WorkingStepIconSvg } from 'components/Common/WorkingSteps/WorkingStepIcon'
import { useLocalizedWorkingStepLabel } from 'components/Localization/useLocalizedWorkingStepLabel'
import { WorkingStepsController } from 'controllers/WorkingStepsController'
import { useAppController } from 'customHooks/useAppController'
import { bomItemActivitiesSelectors } from 'features/BillOfMaterials/store/selectors/activities/bomItemActivitiesSelectors'
import { WorkingStepResourceSelector } from 'features/BillOfMaterials/WorkingSteps/WorkingStepResourceSelector'
import { useBomItemDetailsDialog } from 'features/BomItemDetailsDialog/useBomItemDetailsDialog'
import { isEqual } from 'lodash'
import { BomItemPointer } from 'model/Project/BomItemPointer'
import {
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import {
  BoMItemActivityDto,
  WorkingStepDto,
  WorkingStepType,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { useAppSelector } from 'store/configureStore'
import { makeStyles } from 'tss-react/mui'
import { useOrganizationAndProjectContext } from 'utils/useOrganizationContext'
import { useShowException } from 'utils/useShowException'
import { WorkingStepWithData } from './types/WorkingStepWithData'
import { NewWorkingStepFormRef, WorkingStepForm } from './WorkingStepForm'
import { WorkingStepSelectorPanel } from './WorkingStepSelectorPanel'

type Props = {
  /**
   * the bom items to which the changes will be applied
   */
  affectedBomItemPointers: Array<BomItemPointer>
  activeWorkingStep: BoMItemActivityDto
  /**
   * use this if you don't want state beeing set in the url
   * used in RoutingHeader to avoid modifying the url since we are dealing with multiple bom items
   * @param workingStepType
   * @returns
   */
  setActiveWorkingStep?: (workingStepType: WorkingStepType) => void
  markDirty: (onSave: () => void | Promise<void>, actionName: string) => void
  markClean: (actionName: string) => void
  /**
   * disables the resource selectors inside the working step form
   * when used in the RoutingHeader, modifiying resources will modify also the headers,
   * therefore we disable it to avoid confusion
   */
  disableResourceSelectors?: boolean
}

const makeEmptyDrafts = () => {
  return {
    addedOrUpdated: [],
    removed: [],
  }
}

const useStyles = makeStyles({ name: 'newWorkingSteps' })((theme) => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    '& .ws__selector': {
      borderTop: `1px solid ${theme.palette.divider}`,
      borderRight: `1px solid ${theme.palette.divider}`,
      '& .MuiAutocomplete-inputRoot': {
        display: 'flex',
        gap: theme.spacing(3),
      },
    },
    '& .ws__active': {
      backgroundColor: theme.palette.action.selected,
      textDecorationColor: theme.palette.primary.main,
    },
    '& .ws__list__root': {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden',
    },
    '& .ws__search': {
      padding: theme.spacing(1),
    },
    '& .ws__list': {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minWidth: '320px',
    },
    '& .ws__list__container': {
      height: '100%',
      width: '320px',
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'scroll',
      overflowX: 'hidden',
    },
    '& .ws__list__title': {
      padding: theme.spacing(1, 2),
    },
    '& .ws__item': {
      padding: theme.spacing(0.5, 2),
      cursor: 'pointer',
      borderBottom: `1px solid ${theme.palette.divider}`,
      borderRight: `1px solid ${theme.palette.divider}`,
      '&:hover': {
        backgroundColor: theme.palette.action.hover,
      },
    },
    '& .ws__item__description': {
      display: 'flex',
      alignItems: 'center',
      gap: theme.spacing(2),
    },
    '& .ws__item__actions': {
      display: 'flex',
      justifyContent: 'space-between',
      paddingLeft: theme.spacing(5),
      alignItems: 'center',
      '& span': {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
      },
      '& button': {
        marginLeft: 'auto',
      },
    },
    '& .ws__active_ws': {
      borderTop: `2px solid ${theme.palette.divider}`,
      overflow: 'auto',
      backgroundColor: theme.palette.background.paper,
      height: '100%',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
    },
    '& .ws__active__data': {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
      '& .ws__active__data__header': {
        padding: theme.spacing(1, 2),
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center',
      },
      '& .ws__active__data__resource': {
        padding: theme.spacing(0, 2),
        display: 'flex',
        gap: theme.spacing(2),
        flexDirection: 'column',
      },
      borderRight: `1px solid ${theme.palette.divider}`,
    },

    '& .ws__save__actions': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
      marginTop: 'auto',
      position: 'sticky',
      bottom: 0,
      backgroundColor: theme.palette.background.paper,
      padding: theme.spacing(1),
      borderTop: `2px solid ${theme.palette.divider}`,
      borderRight: `1px solid ${theme.palette.divider}`,
    },

    '& .ws__form': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      justifyContent: 'center',
      paddingBottom: theme.spacing(4),
      height: '100%',
      width: '100%',
    },
    '& .ws__form__validations': {
      // width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      justifySelf: 'flex-start',
      paddingLeft: theme.spacing(2),
      gap: theme.spacing(1),
    },
    // '& .ws__form__input': {
    //   '& input': {
    //     fontSize: theme.typography.caption.fontSize,
    //     padding: theme.spacing(0.2, 0, 0),
    //     '&[type="number"]': {
    //       textAlign: 'right',
    //     },
    //   },
    // },
    '& .ws__form__wrapper': {
      width: '100%',
      height: '100%',
    },
    '& .ws__active__editing__list': {
      '& li': {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: theme.spacing(0.5, 1),
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
  },
}))

export const WorkingStepsContent = (props: Props) => {
  const { classes } = useStyles()
  const { t } = useTranslation()
  const { affectedBomItemPointers, markClean, markDirty } = props

  const workingStepFormRef = useRef<NewWorkingStepFormRef>(null)

  const openDetails = useBomItemDetailsDialog()

  const getLocalizedWorkingStep = useLocalizedWorkingStepLabel()

  const { projectId } = useOrganizationAndProjectContext()

  const { controller, loading } = useAppController(() => {
    return new WorkingStepsController()
  })
  const ShowException = useShowException(t('project:working-steps'))

  const drafts = useRef<{
    addedOrUpdated: WorkingStepWithData[]
    removed: WorkingStepWithData[]
  }>(makeEmptyDrafts())

  const isMounted = useRef(true)
  useEffect(() => {
    isMounted.current = true
    return () => {
      drafts.current = makeEmptyDrafts()
      isMounted.current = false
    }
  }, [])

  const [availableWorkingStepsType, setAvailableWorkingStepTypes] = useState<
    WorkingStepType[]
  >([])

  const [editableWorkingSteps, setEditableWorkingSteps] = useState<
    WorkingStepDto[]
  >([])

  const originalWorkingStepsFromStore = useAppSelector(
    bomItemActivitiesSelectors.allActivities(affectedBomItemPointers[0]),
    isEqual,
  )

  const [activeWorkingStep, setActiveWorkingStep] =
    useState<WorkingStepWithData | null>(props.activeWorkingStep)

  const { setActiveWorkingStep: setActiveWorkingStepProp } = props

  const handleOpenDetails = useCallback(
    (
      bomItemPointer: BomItemPointer,
      bomItemPointers: Array<BomItemPointer>,
      workingStepType: WorkingStepType,
    ) => {
      if (setActiveWorkingStepProp) {
        setActiveWorkingStepProp(workingStepType)
      } else {
        openDetails('workingsteps', {
          bomItemPointer: bomItemPointer,
          affectedBomItemPointers: bomItemPointers,
          workingStep: workingStepType,
        })
      }
    },
    [openDetails, setActiveWorkingStepProp],
  )

  useEffect(() => {
    if (props.activeWorkingStep) {
      const alreadyEditedWs = drafts.current.addedOrUpdated.find(
        (x) =>
          x?.secondaryWorkingStep ===
          props.activeWorkingStep?.secondaryWorkingStep,
      )
      if (alreadyEditedWs) {
        setActiveWorkingStep(alreadyEditedWs)
        return
      }

      const existing = editableWorkingSteps.find(
        (x) =>
          x?.secondaryWorkingStep ===
          props.activeWorkingStep?.secondaryWorkingStep,
      )

      if (existing) {
        setActiveWorkingStep(existing)
        return
      }

      // fallback to the first editable working step
      if (editableWorkingSteps.length > 0) {
        handleOpenDetails(
          props.affectedBomItemPointers[0],
          props.affectedBomItemPointers,
          editableWorkingSteps?.[0].secondaryWorkingStep,
        )
        return
      }

      // if no editable working step, clear activeWorkingStep
      handleOpenDetails(
        props.affectedBomItemPointers[0],
        props.affectedBomItemPointers,
        null,
      )
      return
    }
  }, [
    editableWorkingSteps,
    handleOpenDetails,
    props.activeWorkingStep,
    props.affectedBomItemPointers,
  ])

  const firstAffectedBomitemPointerId = affectedBomItemPointers?.[0]?.id

  const getAvailableWorkingSteps = useCallback(async () => {
    return controller.GetWorkingSteps(projectId, firstAffectedBomitemPointerId)
  }, [controller, firstAffectedBomitemPointerId, projectId])

  const handleSave = useCallback(async () => {
    try {
      await controller.upInsertAndRemoveWorkingStepsWithData(
        affectedBomItemPointers,
        drafts.current.addedOrUpdated,
        drafts.current.removed,
      )

      setValidations((prev) => {
        const newValidations = { ...prev }

        if (activeWorkingStep) {
          delete newValidations[activeWorkingStep.secondaryWorkingStep]
        }

        return newValidations
      })

      drafts.current = makeEmptyDrafts()
      markClean('handleSaveWorkingStepContent')

      if (isMounted.current) {
        const refreshed = await getAvailableWorkingSteps()

        startTransition(async () => {
          setAvailableWorkingStepTypes(() => {
            if (refreshed.availableWorkingSteps) {
              return refreshed.availableWorkingSteps.map(
                (x) => x?.secondaryWorkingStep,
              )
            }

            return []
          })

          setEditableWorkingSteps(refreshed?.selectedWorkingSteps)

          if (activeWorkingStep) {
            await workingStepFormRef.current?.refreshData()
          }
        })
      }
    } catch (err) {
      ShowException(err)
      throw err
    }
  }, [
    controller,
    affectedBomItemPointers,
    markClean,
    getAvailableWorkingSteps,
    activeWorkingStep,
    ShowException,
  ])

  const [validations, setValidations] = useState<
    Partial<Record<WorkingStepType, RJSFValidationError[]>>
  >({})

  const handleRemoveWorkingStep = useCallback(
    (ws: WorkingStepType) => {
      //remove from drafts
      if (drafts.current.addedOrUpdated.length > 0) {
        drafts.current.addedOrUpdated = drafts.current.addedOrUpdated.filter(
          (x) => x?.secondaryWorkingStep !== ws,
        )
      }

      // if the row originally contains the working step, add to removed drafts
      const originallyHadTheWs = originalWorkingStepsFromStore.find(
        (x) => x?.secondaryWorkingStep === ws,
      )

      if (originallyHadTheWs) {
        drafts.current.removed.push(originallyHadTheWs)
        markDirty(handleSave, 'handleSaveWorkingStepContent')
      }

      // it will be available for selection
      setAvailableWorkingStepTypes((prev) => [...prev, ws])

      // remove it from the editable list
      setEditableWorkingSteps((prev) => {
        return prev.filter((x) => x?.secondaryWorkingStep !== ws)
      })
    },
    [handleSave, markDirty, originalWorkingStepsFromStore],
  )

  const handleAddWorkingStep = useCallback(
    (workingStepType: WorkingStepType) => {
      // if already exists in the originally loaded working steps
      // be sure it is not in the removed drafts
      const originalWs = originalWorkingStepsFromStore.find(
        (x) => x?.secondaryWorkingStep === workingStepType,
      )

      if (originalWs) {
        drafts.current.removed = drafts.current.removed.filter(
          (x) => x?.secondaryWorkingStep !== workingStepType,
        )
        setEditableWorkingSteps((prev) => [...prev, originalWs])

        handleOpenDetails(
          props.affectedBomItemPointers[0],
          props.affectedBomItemPointers,
          originalWs?.secondaryWorkingStep,
        )
      } else {
        // if not, add to the added drafts and set as activeWorkingStep
        const newWs: WorkingStepWithData = {
          primaryWorkingStep: workingStepType,
          secondaryWorkingStep: workingStepType,
        }

        drafts.current.addedOrUpdated.push(newWs)
        setEditableWorkingSteps((prev) => [...prev, newWs])
        setActiveWorkingStep(newWs)

        handleOpenDetails(
          props.affectedBomItemPointers[0],
          props.affectedBomItemPointers,
          newWs?.secondaryWorkingStep,
        )
      }

      // remove from the available list
      setAvailableWorkingStepTypes((prev) =>
        prev.filter((x) => x !== workingStepType),
      )

      markDirty(handleSave, 'handleSaveWorkingStepContent')
    },
    [
      handleOpenDetails,
      handleSave,
      markDirty,
      originalWorkingStepsFromStore,
      props.affectedBomItemPointers,
    ],
  )

  const handleSetActiveWorkingStep = useCallback(
    (ws: WorkingStepType) => {
      handleOpenDetails(
        props.affectedBomItemPointers[0],
        props.affectedBomItemPointers,
        ws,
      )
    },
    [handleOpenDetails, props.affectedBomItemPointers],
  )

  const handleChangeActiveWorkingStep = useCallback(
    <T extends keyof WorkingStepWithData>(
      field: T,
      value: WorkingStepWithData[T],
    ) => {
      if (!activeWorkingStep) {
        return
      }

      const updated = {
        ...activeWorkingStep,
        [field]: value,
      }

      const existingIndex = drafts.current.addedOrUpdated.findIndex(
        (x) => x?.secondaryWorkingStep === updated.secondaryWorkingStep,
      )
      if (existingIndex >= 0) {
        drafts.current.addedOrUpdated[existingIndex] = updated
      } else {
        drafts.current.addedOrUpdated.push(updated)
      }

      setValidations((prev) => {
        const newValidations = { ...prev }
        if (newValidations) {
          delete newValidations[updated?.secondaryWorkingStep]
        }

        return newValidations
      })

      workingStepFormRef.current?.getValidations()

      setActiveWorkingStep(updated)
      markDirty(handleSave, 'handleSaveWorkingStepContent')
    },
    [activeWorkingStep, handleSave, markDirty],
  )

  useEffect(() => {
    startTransition(() => {
      getAvailableWorkingSteps().then((data) => {
        if (!data) {
          return
        }

        setAvailableWorkingStepTypes(
          data.availableWorkingSteps.map((x) => x?.secondaryWorkingStep),
        )
        setEditableWorkingSteps(data.selectedWorkingSteps)
      })
    })
  }, [getAvailableWorkingSteps])

  const editableWorkingStepsType = useMemo(() => {
    return editableWorkingSteps.map((x) => x?.secondaryWorkingStep)
  }, [editableWorkingSteps])

  const handleResetChanges = useCallback(() => {
    drafts.current = makeEmptyDrafts()
    markClean('handleSaveWorkingStepContent')

    if (activeWorkingStep) {
      setActiveWorkingStep(null)
    }

    if (validations) {
      setValidations({})
    }

    handleOpenDetails(
      props.affectedBomItemPointers[0],
      props.affectedBomItemPointers,
      null,
    )
  }, [
    activeWorkingStep,
    handleOpenDetails,
    markClean,
    props.affectedBomItemPointers,
    validations,
  ])

  const handleUndoRemoveWorkingStep = useCallback(
    (ws: WorkingStepType) => {
      // find the working step in the removed drafts
      const removedWs = drafts.current.removed.find(
        (x) => x?.secondaryWorkingStep === ws,
      )
      if (!removedWs) {
        return
      }

      // remove from the removed drafts
      drafts.current.removed = drafts.current.removed.filter(
        (x) => x?.secondaryWorkingStep !== ws,
      )
      // add back to the editable working steps
      setEditableWorkingSteps((prev) => [...prev, removedWs])
      // remove from the available working steps
      setAvailableWorkingStepTypes((prev) => prev.filter((x) => x !== ws))

      markDirty(handleSave, 'handleSaveWorkingStepCcontent')
    },
    [handleSave, markDirty],
  )

  return (
    <div className={classes.root}>
      <div className={'ws__selector'}>
        <WorkingStepSelectorPanel
          onWorkingStepRemoved={handleRemoveWorkingStep}
          bomItemPointer={props.affectedBomItemPointers[0]}
          currentWorkingSteps={editableWorkingStepsType}
          availableWorkingSteps={availableWorkingStepsType}
          activeWorkingStep={activeWorkingStep?.secondaryWorkingStep}
          onActiveWorkingStepChange={handleSetActiveWorkingStep}
          onWorkingStepAdded={handleAddWorkingStep}
          markDirty={markDirty}
          handleSave={handleSave}
          validations={validations}
          disableResourceSelectors={props.disableResourceSelectors}
          affectedBomItemPointers={props.affectedBomItemPointers}
        />
      </div>
      <div className={'ws__active_ws'}>
        {!activeWorkingStep && (
          <div
            style={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="body2" color="textSecondary">
              {t(
                'project:select-or-add-working-step-to-edit',
                'select or add a working step to edit',
              )}
            </Typography>
          </div>
        )}
        {activeWorkingStep && (
          <div className={'ws__active__data'}>
            <div className={'ws__active__data__header'}>
              <WorkingStepIconSvg
                workingStepType={activeWorkingStep?.secondaryWorkingStep}
                attributes={{ width: '42px', height: '42px' }}
              />
              <Typography fontWeight={'bold'}>
                {getLocalizedWorkingStep(
                  activeWorkingStep?.secondaryWorkingStep,
                )}
              </Typography>
            </div>
            <div className={'ws__active__data__resource'}>
              <div>
                <WorkingStepResourceSelector
                  bomItemPointers={affectedBomItemPointers}
                  workingStepType={activeWorkingStep?.secondaryWorkingStep}
                  useStoreWorkingStep
                  workingStep={activeWorkingStep}
                  fullWidth
                  withLabel
                  disabled={props.disableResourceSelectors}
                />
              </div>
              <TextField
                fullWidth
                placeholder="add any notes for this working step"
                label={t('project:notes', 'notes')}
                value={
                  activeWorkingStep?.comment ??
                  props.activeWorkingStep?.comment ??
                  ''
                }
                onChange={(e) => {
                  handleChangeActiveWorkingStep(
                    'comment',
                    e.currentTarget.value,
                  )
                }}
                onDrop={(e) => {
                  e.preventDefault()
                  const text = e.dataTransfer.getData('text/plain')
                  if (text) {
                    handleChangeActiveWorkingStep('comment', text)
                  }
                }}
              />
            </div>
            <div className={'ws__form'}>
              {activeWorkingStep && (
                <>
                  {validations[activeWorkingStep?.secondaryWorkingStep] && (
                    <>
                      <span className={'ws__form__validations'}>
                        <WarningAmberOutlined color="warning" />
                        <Typography color="error" variant="body2">
                          {t(
                            'project:working-step-form-has-validation-issues',
                            {
                              defaultValue: '{{count}} issues',
                              count:
                                validations[
                                  activeWorkingStep?.secondaryWorkingStep
                                ]?.length || 0,
                            },
                          )}
                        </Typography>
                      </span>
                    </>
                  )}
                  <WorkingStepForm
                    ref={workingStepFormRef}
                    bomItemPointer={props.affectedBomItemPointers[0]}
                    workingStep={activeWorkingStep}
                    onChange={handleChangeActiveWorkingStep}
                    onError={(errors: RJSFValidationError[]) => {
                      setValidations((prev) => {
                        return {
                          ...prev,
                          [activeWorkingStep!.secondaryWorkingStep]: errors,
                        }
                      })
                    }}
                    projectId={projectId}
                    controller={controller}
                    loading={loading}
                  />
                </>
              )}
            </div>
          </div>
        )}
        <div className={'ws__save__actions'}>
          {Object.values(drafts.current).length > 0 && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  width: '100%',
                  // borderTop: (t) => `2px solid ${t.palette.divider}`,
                  // flex: '0 0 52px',
                }}
              >
                <Tooltip
                  placement="top"
                  slotProps={{
                    popper: {
                      disablePortal: true,
                    },
                    tooltip: {
                      sx: { p: 0 },
                    },
                  }}
                  title={
                    drafts.current.addedOrUpdated.length === 0 ? (
                      '' // to avoid showing empty tooltip
                    ) : (
                      <ul className={'ws__active__editing__list'}>
                        {drafts.current.addedOrUpdated?.map((x) => (
                          <li
                            key={x?.secondaryWorkingStep}
                            onClick={() => {
                              handleSetActiveWorkingStep(
                                x?.secondaryWorkingStep,
                              )
                            }}
                          >
                            <WorkingStepIconSvg
                              workingStepType={x?.secondaryWorkingStep}
                              attributes={{ width: '32px', height: '32px' }}
                            />
                            <Typography variant="body2">
                              {getLocalizedWorkingStep(x?.secondaryWorkingStep)}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    )
                  }
                >
                  <span>
                    <ValueLabel
                      label={t('project:additional-working-steps--to-add')}
                      value={drafts.current?.addedOrUpdated?.length || 0}
                    />
                  </span>
                </Tooltip>
                <Tooltip
                  placement="top"
                  slotProps={{
                    popper: {
                      disablePortal: true,
                    },
                    tooltip: {
                      sx: { p: 0 },
                    },
                  }}
                  title={
                    drafts.current.removed.length === 0 ? (
                      '' // to avoid showing empty tooltip
                    ) : (
                      <ul className={'ws__active__editing__list'}>
                        {drafts.current.removed?.map((x) => (
                          <li
                            key={x?.secondaryWorkingStep}
                            onClick={() => {
                              handleUndoRemoveWorkingStep(
                                x?.secondaryWorkingStep,
                              )
                            }}
                          >
                            <WorkingStepIconSvg
                              workingStepType={x?.secondaryWorkingStep}
                              attributes={{ width: '32px', height: '32px' }}
                            />
                            <Typography variant="body2">
                              {getLocalizedWorkingStep(x?.secondaryWorkingStep)}
                            </Typography>
                          </li>
                        ))}
                      </ul>
                    )
                  }
                >
                  <span>
                    <ValueLabel
                      label={t('project:additional-working-steps--to-remove')}
                      value={drafts.current?.removed?.length || 0}
                    />
                  </span>
                </Tooltip>
              </Box>
              <DialogCloseSaveButtons
                onCloseButtonClicked={handleResetChanges}
                onSaveButtonClicked={handleSave}
                buttonsDisabled={
                  drafts.current.addedOrUpdated.length === 0 &&
                  drafts.current.removed.length === 0
                }
                cancelButtonTranslationKey="common:cancel"
              />
            </>
          )}
        </div>
      </div>
      {/* <div>
        <JsonViewer data={drafts.current} />
        <JsonViewer data={validations} />
        <Button onClick={() => workingStepFormRef.current?.getValidations()}>
          Get Validations
        </Button>
      </div> */}
    </div>
  )
}
